'use no memo';

import { useEffect, useEffectEvent, useState } from 'react';

import { DEFAULT_LOCALE, getPrefix } from '#src/lib/i18n.ts';
import {
  getAllowedLocales,
  getSafeLocaleFromUrl,
} from '#src/lib/navigation/getSafeLocaleFromUrl.ts';

/**
 * This redirects to the active language
 * according to the following algo:
 *
 * 1. If the locale is set in url use that
 * 2. Fallback to first browser locale that is supported
 * 3. Fallback to english
 */
export const useBasenameLocale = () => {
  // eslint-disable-next-line no-restricted-properties
  const localeFromUrl = window.location.pathname.split('/')[1] ?? '';
  const prefix =
    localeFromUrl.length > 5 || localeFromUrl.length < 2
      ? undefined
      : getPrefix(localeFromUrl ?? '');

  const [, updateState] = useState({});
  const forceRerender = () => updateState({});

  // eslint-disable-next-line no-restricted-properties
  const safeLocale = getSafeLocaleFromUrl(window.location.pathname);
  const localeInitialized =
    safeLocale === DEFAULT_LOCALE || prefix === `/${localeFromUrl}`;

  const getPrefixFromLocale = (locale: string | null) => {
    const newAllowedLocale = getAllowedLocales(locale);
    return newAllowedLocale && getPrefix(newAllowedLocale);
  };

  const replaceHistoryState = (newPrefix: string) => {
    // eslint-disable-next-line no-restricted-properties
    const { pathname, search, hash } = window.location;
    // eslint-disable-next-line no-restricted-properties
    window.history.replaceState(
      null,
      '',
      `${newPrefix}${prefix ? pathname.replace(/\/.*?\//, '/') : pathname}${search}${hash}`
    );
    // force rerender to signal the prefix update
    forceRerender();
  };
  const setLocale = (locale: string | null) => {
    const newPrefix = getPrefixFromLocale(locale);
    if (newPrefix) {
      replaceHistoryState(newPrefix);
    }
  };

  const initializeLocale = useEffectEvent(() => {
    const newPrefix = getPrefixFromLocale(safeLocale);
    if (newPrefix) {
      replaceHistoryState(newPrefix);
    }
  });

  useEffect(() => {
    if (localeInitialized) {
      return;
    }
    initializeLocale();
  }, [localeInitialized]);

  return {
    locale: safeLocale,
    setLocale,
    basename: prefix,
    loading: !localeInitialized,
  };
};
